import { useCallback, useMemo } from 'react';
import { replaceQuery, useCurrentSearch } from '@/app/router';
import type { FunnelStatus, FunnelSort } from '@/contracts/funnel';

export interface DashboardQueryState {
  q: string;
  status: FunnelStatus;
  favorite: boolean;
  folderId: string | null;
  tagId: string | null;
  sort: FunnelSort;
  page: number;
}

const DEFAULTS: DashboardQueryState = {
  q: '',
  status: 'active',
  favorite: false,
  folderId: null,
  tagId: null,
  sort: 'updated_desc',
  page: 1,
};

/**
 * SPEC-003 plan §28 "URL State": dashboard filters live in the query
 * string so reload/back preserve the view, without adding a router
 * dependency.
 */
export function useFunnelListQuery(): [DashboardQueryState, (patch: Partial<DashboardQueryState>) => void] {
  const searchString = useCurrentSearch();

  const state = useMemo<DashboardQueryState>(() => {
    const search = new URLSearchParams(searchString);
    return {
      q: search.get('q') ?? DEFAULTS.q,
      status: (search.get('status') as FunnelStatus | null) ?? DEFAULTS.status,
      favorite: search.get('favorite') === 'true',
      folderId: search.get('folder'),
      tagId: search.get('tag'),
      sort: (search.get('sort') as FunnelSort | null) ?? DEFAULTS.sort,
      page: Number(search.get('page') ?? DEFAULTS.page),
    };
  }, [searchString]);

  const setState = useCallback(
    (patch: Partial<DashboardQueryState>) => {
      const next: DashboardQueryState = { ...state, ...patch };
      // Any filter change other than an explicit page bump resets to page 1.
      if (!('page' in patch)) next.page = 1;

      const params = new URLSearchParams();
      if (next.q) params.set('q', next.q);
      if (next.status !== DEFAULTS.status) params.set('status', next.status);
      if (next.favorite) params.set('favorite', 'true');
      if (next.folderId) params.set('folder', next.folderId);
      if (next.tagId) params.set('tag', next.tagId);
      if (next.sort !== DEFAULTS.sort) params.set('sort', next.sort);
      if (next.page !== DEFAULTS.page) params.set('page', String(next.page));

      replaceQuery(params);
    },
    [state],
  );

  return [state, setState];
}
